import {Claims, IdentityUser, IdentityUserTenantRole, MyInformationResponseDto, Roles} from "@/@types/auth.types.ts";

export const emptyTenant = '00000000-0000-0000-0000-000000000000';

// Keycloak realm roles for this application are namespaced with this prefix (the realm is shared
// across apps, so we only ever look at owninn_* roles).
const OWNINN_ROLE_PREFIX = 'owninn_';
const SUPER_ADMIN_REALM_ROLE = 'owninn_superadmin';

const parseJwtTokenClaims = (token: string): Claims => {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
        atob(base64)
            .split("")
            .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join(""),
    );

    return JSON.parse(jsonPayload);
};

// Filter the Keycloak realm roles down to this application's owninn_* roles.
const getOwnInnRealmRoles = (claims: Claims): string[] =>
    (claims.realm_access?.roles ?? []).filter((role) => role.startsWith(OWNINN_ROLE_PREFIX));

const createUser = (user: MyInformationResponseDto, claims: Claims): IdentityUser | null => {
    const realmRoles = getOwnInnRealmRoles(claims);

    // SuperAdmin is a global (realm) role; it short-circuits tenant-scoped roles.
    if (realmRoles.includes(SUPER_ADMIN_REALM_ROLE)) {
        return {
            userName: user.userName,
            avatarUrl: user.avatarUrl,
            roles: [{tenantId: emptyTenant, role: Roles.superAdmin}],
        };
    }

    // Tenant-scoped roles now come from the API response (the Keycloak token only carries
    // global owninn_ realm roles).
    const tenantRoles: IdentityUserTenantRole[] = (user.tenantRoles ?? []).map((tenantRole) => ({
        tenantId: tenantRole.tenantId,
        role: tenantRole.role as Roles,
    }));

    return {
        userName: user.userName,
        avatarUrl: user.avatarUrl,
        roles: tenantRoles,
    };
};

export const AuthHelper = {
    parseJwtTokenClaims,
    createUser,
};
